import { useEffect, useState } from "react";
import { RotateCcw, ArrowLeftCircle, Building2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RupiahInput from "@/components/RupiahInput";
import DateField from "@/components/DateField";
import { formatIDR } from "@/lib/constants";

/**
 * PettyCashReturnDialog — staff mengembalikan sisa kas ke perusahaan.
 * Constraint (08-Feb-2026): "nominal pengembalian tidak bisa lebih besar daripada sisa
 * uang yang dimiliki". Validasi ganda — client (button disabled + error text) + server
 * (HTTP 400).
 * User request 08-Feb-2026 (turn 5): "tambahkan pilihan nomor rekening perusahaan pada
 * pilihan Pengembalian Kas".
 */
export default function PettyCashReturnDialog({ open, currentBalance = 0, effectiveCap, pendingReturns = 0, onCancel, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [returnDate, setReturnDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [companyBanks, setCompanyBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState("");
  const [loadingBanks, setLoadingBanks] = useState(false);

  // Fetch company bank accounts whenever dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingBanks(true);
    api.get("/company-bank-accounts")
      .then(({ data }) => {
        if (cancelled) return;
        setCompanyBanks(data || []);
        // Auto-select first bank if only one exists
        if ((data || []).length === 1) setSelectedBankId(data[0].id);
      })
      .catch(() => { if (!cancelled) setCompanyBanks([]); })
      .finally(() => { if (!cancelled) setLoadingBanks(false); });
    return () => { cancelled = true; };
  }, [open]);

  const amountNum = Number(String(amount).replace(/[^\d.-]/g, "")) || 0;
  // Use effective cap (balance − pending returns) if provided by parent; falls back to raw balance.
  const cap = typeof effectiveCap === "number" ? effectiveCap : currentBalance;
  const isFull = amountNum > 0 && Math.abs(amountNum - cap) < 0.01;
  const overLimit = amountNum > cap + 0.005;
  const remaining = Math.max(0, cap - amountNum);
  const noBanks = !loadingBanks && companyBanks.length === 0;

  const reset = () => {
    setAmount("");
    setReturnDate(new Date().toISOString().slice(0, 10));
    setNote("");
    setSelectedBankId(companyBanks.length === 1 ? companyBanks[0].id : "");
  };

  const submit = async () => {
    if (!amountNum || amountNum <= 0) { toast.error("Nominal pengembalian wajib diisi"); return; }
    if (overLimit) { toast.error(`Nominal melebihi saldo (${formatIDR(currentBalance)})`); return; }
    if (!returnDate) { toast.error("Tanggal pengembalian wajib diisi"); return; }
    if (!selectedBankId) { toast.error("Rekening perusahaan tujuan pengembalian wajib dipilih"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/petty-cash/returns", {
        amount: amountNum,
        return_date: returnDate,
        note: note || null,
        company_bank_account_id: selectedBankId,
      });
      toast.success(`Pengembalian Rp${amountNum.toLocaleString("id-ID")} terkirim — menunggu verifikasi FAT/BOD`);
      reset();
      onSuccess?.();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onCancel(); } }}>
      <DialogContent className="rounded-none max-w-md" data-testid="dlg-pc-return">
        <DialogHeader>
          <DialogTitle className="font-display text-xl inline-flex items-center gap-2">
            <ArrowLeftCircle size={20} className="text-emerald-700" />
            Pengembalian Kas
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="border-l-2 border-emerald-500 bg-emerald-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-800 font-semibold">Saldo Anda Saat Ini</div>
            <div className="tabular-nums text-2xl font-bold text-emerald-800" data-testid="pc-return-current-balance">{formatIDR(currentBalance)}</div>
            {pendingReturns > 0 && (
              <div className="mt-1 text-[10px] text-amber-800 border-t border-emerald-200 pt-1">
                Ada pengembalian <b>PENDING</b> menunggu verifikasi: <span className="tabular-nums font-semibold">{formatIDR(pendingReturns)}</span>
                <div className="tabular-nums text-emerald-900">Sisa yang bisa Anda ajukan: <b>{formatIDR(cap)}</b></div>
              </div>
            )}
            <div className="text-[10px] text-emerald-700 mt-0.5">Nominal pengembalian tidak boleh melebihi angka {pendingReturns > 0 ? "sisa yang bisa diajukan" : "saldo"} di atas.</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Nominal Pengembalian *</label>
            <RupiahInput
              testid="input-return-amount"
              value={amount}
              onChange={setAmount}
              placeholder="0"
              className="h-10"
            />
            <div className="flex justify-between mt-1 text-[10px]">
              <span className="text-slate-500">
                Sisa setelah dikembalikan: <span className={`font-semibold tabular-nums ${remaining >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="pc-return-remaining">{formatIDR(remaining)}</span>
              </span>
              <button
                type="button"
                data-testid="btn-return-fill-all"
                onClick={() => setAmount(String(cap))}
                className="text-[#FF5A1F] hover:underline font-semibold uppercase tracking-wider"
                disabled={cap <= 0}
              >
                Isi penuh
              </button>
            </div>
            {overLimit && (
              <div className="text-[10px] text-rose-700 mt-1 font-semibold" data-testid="pc-return-error-over">
                ⚠ Nominal melebihi saldo yang tersedia.
              </div>
            )}
            {isFull && !overLimit && (
              <div className="text-[10px] text-amber-700 mt-1" data-testid="pc-return-full-notice">
                ℹ Anda akan mengembalikan seluruh saldo — kas akan menjadi 0.
              </div>
            )}
          </div>

          {/* Rekening perusahaan tujuan pengembalian */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1">
              <Building2 size={11} /> Rekening Perusahaan Tujuan *
            </label>
            {loadingBanks ? (
              <div className="mt-1 h-9 text-xs text-slate-500 italic px-2 flex items-center border border-slate-200 bg-slate-50">Memuat daftar rekening…</div>
            ) : noBanks ? (
              <div className="mt-1 text-[10px] text-amber-800 bg-amber-50 border-l-2 border-amber-400 p-2 inline-flex items-start gap-1">
                <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
                <span>Belum ada rekening perusahaan terdaftar. Minta BOD/FAT menambahkan rekening perusahaan di Admin → Bank Accounts terlebih dahulu.</span>
              </div>
            ) : (
              <select
                data-testid="select-return-company-bank"
                value={selectedBankId}
                onChange={(e) => setSelectedBankId(e.target.value)}
                className="rounded-none mt-1 h-9 w-full border border-slate-300 bg-white px-2 text-xs"
              >
                <option value="">— pilih rekening tujuan —</option>
                {companyBanks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bank_name} — {b.account_no}{b.account_name ? ` (${b.account_name})` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Tanggal Pengembalian *</label>
            <div className="mt-1">
              <DateField testid="dp-return-date" value={returnDate} onChange={(v) => setReturnDate(v)} />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Catatan (opsional)</label>
            <Textarea
              data-testid="input-return-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="rounded-none mt-1 text-sm"
              rows={2}
              placeholder="mis. sisa kas minggu 44 ditransfer via BCA"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onCancel(); }} className="rounded-none">Batal</Button>
          <Button
            data-testid="btn-confirm-return"
            onClick={submit}
            disabled={busy || overLimit || amountNum <= 0 || cap <= 0 || !selectedBankId}
            className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white inline-flex items-center gap-1.5"
          >
            <RotateCcw size={13} /> {busy ? "Menyimpan…" : "Kirim ke Approver"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
